import { Product } from '../../../../shared/interfaces/product';
import { ProductFamily } from '../interfaces/product-family';
import { MULTI_SPACE } from '../consts/multi-space';
import { TOKEN_PATTERN } from '../consts/token-pattern';

export class ProductFamiliesUtil {
    public static chipLabelsForVariants(variants: Product[]): Map<number, string> {
        const isColorPicker = ProductFamiliesUtil.isColorPickerFamily(variants);
        const out = new Map<number, string>();
        variants.forEach((v) => {
            const fallback = v.part_number ?? String(v.id);
            out.set(v.id, isColorPicker ? (ProductFamiliesUtil.colorOf(v) as string) : fallback);
        });
        return out;
    }

    public static colorOf(product: Product): string | null {
        if (product.variant_color) {
            return product.variant_color;
        }
        return ProductFamiliesUtil.extractColorFromTitle(product.title ?? '').color;
    }

    public static deriveFamilyKey(title: string): string {
        return ProductFamiliesUtil.extractColorFromTitle(title).stem.toLowerCase();
    }

    public static familyKeyFor(product: Product): string {
        if (product.family_key) {
            return product.family_key;
        }
        return ProductFamiliesUtil.deriveFamilyKey(product.title ?? '');
    }

    public static groupByFamily(products: Product[]): ProductFamily[] {
        // Group by the backend-provided card_id so the frontend agrees with the
        // backend's CARD_EXPR exactly: color-picker families collapse into one
        // group, everything else is a singleton. Falls back to family_key
        // derivation when card_id is absent (older API responses, unit tests).
        const families = new Map<string, ProductFamily>();
        for (const product of products) {
            const key = product.card_id ?? ProductFamiliesUtil.familyKeyFor(product);
            const existing = families.get(key);
            if (existing) {
                existing.variants.push(product);
                continue;
            }
            families.set(key, { primary: product, variants: [product], family_key: key });
        }
        return [...families.values()];
    }

    public static isColorPickerFamily(variants: Product[]): boolean {
        const colors = variants.map((v) => ProductFamiliesUtil.colorOf(v));
        if (!colors.every((c) => c !== null && c !== '')) {
            return false;
        }
        const counts = new Map<string, number>();
        for (const c of colors) {
            if (c) {
                counts.set(c, (counts.get(c) ?? 0) + 1);
            }
        }
        return [...counts.values()].every((n) => n === 1);
    }

    private static extractColorFromTitle(title: string): { color: string | null; stem: string } {
        const cleaned = title.replace(/[®™©]/g, '');
        const match = cleaned.match(TOKEN_PATTERN);
        if (!match) {
            return { color: null, stem: cleaned.trim() };
        }
        const stem = cleaned.replace(TOKEN_PATTERN, '').replace(MULTI_SPACE, ' ').trim();
        return { color: match[1], stem };
    }
}
